import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MarketLayout from '../components/MarketLayout';
import StatCard from '../components/StatCard';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_LABELS = {
  fund: 'Credit Fund',
  family_office: 'Family Office',
  reit: 'REIT',
  trade_desk: 'Trade Desk',
  individual: 'Individual',
  other: 'Other',
};

function fmtM(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function timeAgo(d) {
  if (!d) return '—';
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PRIORITY_DOT = { urgent: '#ff082f', high: '#ff082f', normal: '#3a6ad9', low: '#9ca3af' };

// Stroke SVG icons replace inline emojis on the market admin dashboard so
// the iconography stays on-brand and renders identically across platforms
// (emoji glyphs vary wildly between Apple, Windows, Android, and email
// clients). Each Icon takes width/height/stroke via props with sensible
// defaults.
const baseIconProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const FolderIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);
const DollarIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M12 2v20" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const ArrowUpIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);
const UsersIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const UserIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const DocumentIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </svg>
);
const LockIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const BellIcon = ({ size = 18, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseIconProps} stroke={color || 'currentColor'} aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// Maps the alert.type tag to the matching icon component. Falls back to
// the bell icon for unknown types (forward-compat as new event types ship).
const renderAlertIcon = (type, color) => {
  const Cmp = type === 'investor' ? UserIcon
    : type === 'bid'      ? DollarIcon
    : type === 'loi'      ? DocumentIcon
    : type === 'nda'      ? LockIcon
    : BellIcon;
  return <Cmp size={18} color={color} />;
};

export default function MarketDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [stats, setStats] = useState({
    activePools: 0,
    totalUpbListed: 0,
    openBids: 0,
    approvedInvestors: 0,
  });
  const [pendingInvestors, setPendingInvestors] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const auth = { headers: { Authorization: `Bearer ${token}` } };
        const [statsRes, alertsRes] = await Promise.all([
          axios.get(`${API}/api/admin/market/stats`, auth),
          axios.get(`${API}/api/admin/market/alerts?limit=10`, auth).catch(() => ({ data: { alerts: [] } })),
        ]);
        if (!mounted) return;
        setStats(statsRes.data.stats || stats);
        setPendingInvestors(statsRes.data.recentPendingInvestors || []);
        setRecentAlerts(alertsRes.data.alerts || []);
        setUnreadCount((alertsRes.data.alerts || []).filter((a) => !a.is_read).length);
      } catch (err) {
        console.error('[Market] stats load failed:', err.response?.data?.error || err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MarketLayout>
      <div style={{ padding: '28px 32px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>DoorlyMarket — Overview</h1>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>Secondary market dashboard</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard
            label="Active Pools"
            value={loading ? '—' : String(stats.activePools)}
            icon={<FolderIcon color="#111111" />}
            color="#acf44e"
            bg="#f0fde4"
            onClick={() => navigate('/admin/market/loans')}
          />
          <StatCard
            label="Total UPB Listed"
            value={loading ? '—' : fmtM(stats.totalUpbListed)}
            icon={<DollarIcon color="#111111" />}
            color="#acf44e"
            bg="#f0fde4"
          />
          <StatCard
            label="Open Bids"
            value={loading ? '—' : String(stats.openBids)}
            icon={<ArrowUpIcon color="#ff082f" />}
            color="#ff082f"
            bg="#ffe6e9"
            onClick={() => navigate('/admin/market/funding')}
          />
          <StatCard
            label="Investors Approved"
            value={loading ? '—' : String(stats.approvedInvestors)}
            icon={<UsersIcon color="#3a6ad9" />}
            color="#3a6ad9"
            bg="#eff4ff"
            onClick={() => navigate('/admin/market/investors')}
          />
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Recent Activity</span>
              {unreadCount > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 700, color: '#fff',
                  background: '#3a6ad9', padding: '2px 8px', borderRadius: 9999,
                }}>
                  {unreadCount} unread
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/admin/market/alerts')}
              style={{ fontSize: 12, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              View all →
            </button>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
          ) : recentAlerts.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No recent activity.
            </div>
          ) : (
            <div>
              {recentAlerts.map((a) => (
                <div
                  key={a.id}
                  onClick={() => navigate('/admin/market/alerts')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 20px',
                    borderTop: '1px solid #f9fafb',
                    cursor: 'pointer',
                    backgroundColor: a.is_read ? 'transparent' : '#f8faff',
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = a.is_read ? 'transparent' : '#f8faff')}
                >
                  <div style={{ flexShrink: 0, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', color: PRIORITY_DOT[a.priority] || PRIORITY_DOT.normal }}>
                    {renderAlertIcon(a.type, PRIORITY_DOT[a.priority] || PRIORITY_DOT.normal)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#111827', fontWeight: a.is_read ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.description}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                      {a.related_record ? `${a.related_record} · ` : ''}{timeAgo(a.created_at)}
                    </div>
                  </div>
                  <div
                    aria-hidden
                    style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: PRIORITY_DOT[a.priority] || PRIORITY_DOT.normal,
                      opacity: a.is_read ? 0.3 : 1,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Pending Investor Registrations</span>
            <button
              onClick={() => navigate('/admin/market/investors')}
              style={{ fontSize: 12, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              View all →
            </button>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
          ) : pendingInvestors.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No pending registrations.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  {['Name', 'Email', 'Firm', 'Type', 'Registered'].map((h) => (
                    <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.05 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingInvestors.map((inv) => (
                  <tr
                    key={inv.id}
                    style={{ borderTop: '1px solid #f9fafb', cursor: 'pointer' }}
                    onClick={() => navigate('/admin/market/investors')}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {inv.first_name} {inv.last_name}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>{inv.email}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151' }}>{inv.firm_name || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>
                      {TYPE_LABELS[inv.investor_type] || inv.investor_type || '—'}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#9ca3af' }}>{fmtDate(inv.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </MarketLayout>
  );
}
